/** @jsx jsx */
import React, {
	type ReactNode,
	useRef,
	useEffect,
	useCallback,
	forwardRef,
	type ForwardedRef,
} from 'react';
import { css, jsx } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import {
	getDelta,
	useResizeObserver,
} from '@atlassian/jira-portfolio-3-common/src/custom-scrollbar';
import { useActions } from '../../controllers';
import { getNativeScrollbarSizeMemoed } from './utils';

interface Props {
	name: string;
	width?: number;
	minWidth?: number;
	children: ReactNode | ReactNode[];
	primary?: boolean;
}

const paddingToHideAutoHideScrollbar = 50;

export const PayloadUpdater = ({
	name,
	containerRef,
	viewportRef,
}: {
	name: string;
	containerRef: React.RefObject<HTMLDivElement | null>;
	viewportRef: React.RefObject<HTMLDivElement | null>;
}) => {
	const { setPayload, resetPayload } = useActions();

	useResizeObserver(
		containerRef,
		useCallback(
			(el) => {
				const { width: scrollWidth } = el.getBoundingClientRect();
				setPayload(name, (data) => ({ ...data, scrollWidth }));
			},
			[name, setPayload],
		),
	);

	useResizeObserver(
		viewportRef,
		useCallback(
			(el) => {
				const { width: viewportWidth } = el.getBoundingClientRect();
				setPayload(name, (data) => ({ ...data, viewportWidth }));
			},
			[name, setPayload],
		),
	);

	useEffect(
		() => () => {
			resetPayload(name);
		},
		[resetPayload, name],
	);

	return null;
};

export const HorizontalSyncedScrollView = ({ name, primary, width, minWidth, children }: Props) => {
	const viewportRef = useRef<HTMLDivElement>(null);
	const scrollableRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const { setOffset, setOffsetFallback, removeScrollElement, addScrollElement } = useActions();

	const nativeScrollBarHeight = getNativeScrollbarSizeMemoed();

	const handleScroll = useCallback(() => {
		if (scrollableRef.current) {
			const deltaX = scrollableRef.current.scrollLeft;

			setOffsetFallback(name, deltaX);
		}
	}, [setOffsetFallback, name]);

	const handleWheel = useCallback(
		(e: WheelEvent) => {
			const { deltaX, deltaY } = getDelta(e);

			if (Math.abs(deltaX) >= Math.abs(deltaY)) {
				setOffset(name, (offset: number) => offset + deltaX);
				e.preventDefault();
			}
		},
		[name, setOffset],
	);

	useEffect(() => {
		if (!scrollableRef.current) {
			return;
		}
		const scrollableEl = scrollableRef.current;

		scrollableEl.addEventListener('wheel', handleWheel, false);
		scrollableEl.addEventListener('scroll', handleScroll, false);
		addScrollElement(name, scrollableEl);
		return () => {
			scrollableEl.removeEventListener('wheel', handleWheel, false);
			scrollableEl.removeEventListener('scroll', handleScroll, false);
			removeScrollElement(name, scrollableEl);
		};
	}, [scrollableRef, handleWheel, handleScroll, addScrollElement, removeScrollElement, name]);

	return (
		<>
			<Viewport ref={viewportRef} width={width} minWidth={minWidth}>
				<Scrollable
					ref={scrollableRef}
					bottom={-((nativeScrollBarHeight ?? 0) + paddingToHideAutoHideScrollbar)}
				>
					<Box xcss={containerStyles} ref={containerRef}>
						{children}
					</Box>
				</Scrollable>
			</Viewport>
			{primary && (
				<PayloadUpdater name={name} containerRef={containerRef} viewportRef={viewportRef} />
			)}
		</>
	);
};

const Scrollable = forwardRef(
	(
		{
			bottom,
			children,
		}: {
			bottom: number;
			children: ReactNode;
		},
		ref: ForwardedRef<HTMLDivElement>,
	) => (
		<div
			ref={ref}
			data-testid="portfolio-3-horizontal-scrolling-synced.ui.scroll-view.scrollable"
			css={scrollableStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ bottom: `${bottom}px` }}
		>
			{children}
		</div>
	),
);

const scrollableStyles = css({
	position: 'absolute',
	top: 0,
	right: 0,
	left: 0,

	paddingBottom: `${paddingToHideAutoHideScrollbar}px`,
	overflowX: 'scroll',
	outline: 'none',
	/** To make sure the inner div width is not affected by this div */
	display: 'flex',
});

const Viewport = forwardRef(
	(
		{
			width,
			minWidth,
			children,
		}: {
			width?: number;
			minWidth?: number;
			children: ReactNode;
		},
		ref: ForwardedRef<HTMLDivElement>,
	) => (
		<div
			css={viewportStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				width: width ? `${width}px` : 'auto',
				minWidth: minWidth ? `${minWidth}px` : '0px',
			}}
			ref={ref}
		>
			{children}
		</div>
	),
);

const viewportStyles = css({
	position: 'relative',
	overflowY: 'hidden',
	height: '100%',
});

const containerStyles = xcss({
	height: '100%',
});
